/**
 * @fileoverview Componente de cuadrícula de productos para selección modal
 * @module ProductGrid
 * @category Component - Molecule
 * @since 1.0.0
 * @version 1.0.0
 */
import React, {
  useEffect, useRef, useCallback, memo
} from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import ProductGridItem from './ProductGridItem';

import useProductsApi from '../../../../hooks/useProductsApi';
import SearchMC from '../../../components/SearchMC';
import LoadingMC from '../../../components/LoadingMC';
import ButtonComponent from '../../../components/ButtonMC';

/**
 * @typedef {Object} Product
 * @description Objeto producto con información completa
 * @property {string} id - Identificador único del producto
 * @property {string} web_id - ID web del producto
 * @property {string} title - Título del producto
 * @property {string} description - Descripción del producto
 * @property {number} price - Precio base del producto
 * @property {string} currency - Código de moneda
 * @property {number} quantity - Stock disponible
 * @property {Array} image_urls - URLs de imágenes del producto
 * @property {Array} product_variant_combinations - Variantes del producto
 * @property {boolean} active - Estado activo del producto
 */

/**
 * @typedef {Object} Category
 * @description Categoría de productos
 * @property {string} web_id - ID web de la categoría
 * @property {string} name - Nombre de la categoría
 */

/**
 * Componente Molecule para mostrar cuadrícula de productos.
 *
 * Implementa el patrón **Molecule** de Atomic Design con funcionalidad
 * completa de búsqueda, filtrado y paginación de productos.
 *
 * @component ProductGrid
 * @category Component - Molecule
 *
 * @description
 * **Características:**
 * - Búsqueda en tiempo real con debounce
 * - Filtrado por categorías
 * - Paginación con scroll infinito
 * - Diseño responsivo con Tailwind CSS
 * - Hooks personalizados para gestión de estado
 * - Optimización de renders con React.memo
 *
 * **Atomic Design:**
 * - **Nivel**: Molecule
 * - **Composición**: Utiliza ProductGridItem (Atom)
 * - **Dependencias**: Hooks personalizados, react-select
 *
 * @param {Object} props - Propiedades del componente
 * @param {function} props.onProductSelect - Callback ejecutado al seleccionar producto
 * @param {function} props.onOpenCategoryFilter - Callback para abrir filtros de categorías
 * @param {Array<Object>} props.selectedCategories - Categorías seleccionadas para filtrar
 *
 * @returns {JSX.Element} Elemento JSX renderizado con grid de productos
 *
 * @example
 * ```jsx
 * <ProductGrid
 *   onProductSelect={(product) => console.log('Selected:', product)}
 *   onOpenCategoryFilter={() => console.log('Open filters')}
 *   selectedCategories={[{web_id: 'cat1', name: 'Categoria 1'}]}
 * />
 * ```
 */
const ProductGrid = memo(({
  onProductSelect,
  onOpenCategoryFilter,
  selectedCategories = [],
  columns = 3,
  searchValue = '',
  onSearchChange
}) => {
  const { t } = useTranslation('Products');

  // Hooks API especializados
  const {
    products,
    loading: productsLoading,
    totalPages,
    fetchProducts
  } = useProductsApi();

  // Estados derivados
  const loading = productsLoading;

  // Ref para controlar el primer render
  const isFirstRender = useRef(true);
  const hasExecutedInitialSearch = useRef(false);
  const scrollContainerRef = useRef(null);
  const isScrollFetchingRef = useRef(false);
  const lastFetchedPageRef = useRef(0);

  const totalPagesRef = useRef(totalPages);
  totalPagesRef.current = totalPages;

  /**
   * Efecto principal para búsquedas
   */
  useEffect(() => {
    const executeSearch = async () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }

      const params = {
        page: 1,
        'q[active_eq]': true
      };

      // Agregar búsqueda por texto si existe
      if (searchValue) {
        params['q[title_or_description_or_sku_i_cont]'] = searchValue;
      }

      // Si hay categorías seleccionadas, agregarlas al filtro usando nombres
      if (selectedCategories.length > 0) {
        const categoryNames = selectedCategories.map((category) => category.name);
        if (categoryNames.length === 1) {
          // eslint-disable-next-line prefer-destructuring
          params['q[category_name_eq]'] = categoryNames[0];
        } else {
          params['q[category_name_in]'] = categoryNames;
        }
      }

      await fetchProducts(params);
      lastFetchedPageRef.current = 1;
    };

    if (isFirstRender.current && !hasExecutedInitialSearch.current) {
      isFirstRender.current = false;
      hasExecutedInitialSearch.current = true;
      executeSearch();
    } else if (!isFirstRender.current) {
      // Solo ejecutar después del primer render
      executeSearch();
    }
  }, [searchValue, selectedCategories, fetchProducts]);


  /**
   * Maneja cambios en el input de búsqueda.
   */
  const handleSearchChange = useCallback((val) => {
    onSearchChange?.(val);
  }, [onSearchChange]);

  useEffect(() => {
    lastFetchedPageRef.current = 0;
  }, [searchValue, selectedCategories]);

  // Core infinite-scroll logic extracted so it can be called from both the
  // scroll event handler and the post-load effect without needing a synthetic event.
  const tryLoadNextPage = useCallback(async (container) => {
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const tp = totalPagesRef.current;
    const prevFetched = lastFetchedPageRef.current;
    const nextPage = prevFetched + 1;

    if (
      scrollTop + clientHeight >= scrollHeight - 5 &&
      !isScrollFetchingRef.current &&
      prevFetched < tp
    ) {
      isScrollFetchingRef.current = true;
      lastFetchedPageRef.current = nextPage;

      const params = {
        page: nextPage,
        'q[active_eq]': true
      };

      if (searchValue) {
        params['q[title_or_description_or_sku_i_cont]'] = searchValue;
      }

      if (selectedCategories.length > 0) {
        const categoryNames = selectedCategories.map((category) => category.name);
        if (categoryNames.length === 1) {
          // eslint-disable-next-line prefer-destructuring
          params['q[category_name_eq]'] = categoryNames[0];
        } else {
          params['q[category_name_in]'] = categoryNames;
        }
      }

      try {
        await fetchProducts(params, true);
      } catch (err) {
        lastFetchedPageRef.current = prevFetched;
      } finally {
        isScrollFetchingRef.current = false;
      }
    }
  }, [searchValue, selectedCategories, fetchProducts]);

  // Keep a ref so the post-load effect always calls the latest version of
  // tryLoadNextPage without adding it to the effect's dependency array.
  const tryLoadNextPageRef = useRef(tryLoadNextPage);
  tryLoadNextPageRef.current = tryLoadNextPage;

  // After each page finishes loading, re-check the scroll position. If the user
  // was already at the bottom when loading completed they should not need to
  // scroll again to trigger the next page.
  useEffect(() => {
    if (productsLoading || !scrollContainerRef.current) return;
    if (lastFetchedPageRef.current >= totalPagesRef.current) return;
    tryLoadNextPageRef.current(scrollContainerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsLoading]);

  const handleScroll = useCallback((e) => {
    tryLoadNextPageRef.current(e.target);
  }, []);

  /**
   * Obtiene el precio mínimo para productos con variantes.
   */
  const getProductPrice = useCallback((product) => {
    if (isEmpty(product.product_variant_combinations)) {
      return product.price;
    }

    const prices = product.product_variant_combinations.map(
      (combination) => Number(combination.price)
    );

    return Number(Math.min(...prices));
  }, []);

  return (
    <div className="tw-flex tw-flex-col tw-h-full">
      {/* Header con búsqueda y filtros */}
      <div className="tw-sticky tw-top-0 tw-bg-white tw-z-10 tw-pb-3">
        <div className="tw-flex tw-items-center tw-gap-2">
          {/* Campo de búsqueda */}
          <div className="tw-flex-1">
            <SearchMC
              placeholder={t('productGrid.search.placeholder')}
              value={searchValue}
              debounceTime={500}
              onChange={handleSearchChange}
              inputClassName="tw-w-full"
            />
          </div>

          {/* Botón de filtros */}
          <div className="tw-relative">
            <ButtonComponent
              variant="GRAY"
              icon="filter-3-fill"
              onClick={onOpenCategoryFilter}
              className={selectedCategories.length > 0 ? 'tw-border-blue-500 tw-bg-blue-50' : ''}
              iconClassName={selectedCategories.length > 0 ? 'tw-text-blue-600' : ''}
              size="small"
              outlined
            />

            {/* Indicador de filtros activos */}
            {selectedCategories.length > 0 && (
              <span className="tw-absolute tw--top-1 tw--right-1 tw-bg-blue-600 tw-text-white tw-text-xs tw-rounded-full tw-h-5 tw-w-5 tw-flex tw-items-center tw-justify-center tw-font-medium">
                {selectedCategories.length}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Grid de productos */}
      <div
        ref={scrollContainerRef}
        className="tw-flex-1 tw-overflow-auto"
        onScroll={handleScroll}
      >
        {(() => {
          if (products.length > 0) {
            return (
              <>
                <div className={`tw-grid tw-grid-cols-${columns} tw-gap-4`}>
                  {products.map((product) => (
                    <ProductGridItem
                      key={`product-${product.web_id}`}
                      product={product}
                      currency={`${product.currency}`}
                      price={getProductPrice(product)}
                      onSelect={() => onProductSelect(product)}
                      hasVariants={!isEmpty(product.product_variant_combinations)}
                    />
                  ))}
                </div>

                <LoadingMC
                  loading={productsLoading}
                  className="tw-py-8"
                  message={t('productGrid.loading.more')}
                />
              </>
            );
          }

          if (loading) {
            return (
              <div className="tw-flex tw-items-center tw-justify-center tw-h-64">
                <LoadingMC
                  loading
                  message={t('productGrid.loading.initial')}
                />
              </div>
            );
          }

          return (
            <div className="tw-flex tw-items-center tw-justify-center tw-h-8">
              <span className="tw-text-gray-500 tw-text-sm">
                {searchValue || selectedCategories.length > 0
                  ? t('productGrid.empty.filtered.title')
                  : t('productGrid.empty.general.title')}
              </span>
            </div>
          );
        })()}
      </div>
    </div>
  );
});

/**
 * Nombre de display para debugging y herramientas de desarrollo.
 *
 * @type {string}
 */
ProductGrid.displayName = 'ProductGrid';

export default ProductGrid;
